import { z } from "zod";

export const REASON_CATEGORIES = [
  "location",
  "blocklist",
  "integrity",
  "availability",
  "network",
] as const;
export const ReasonCategory = z.enum(REASON_CATEGORIES).meta({ id: "ReasonCategory" });
export type ReasonCategory = z.infer<typeof ReasonCategory>;

/** Locales of the canonical texts (English is the source; Brazilian Portuguese is kept in sync). */
export const REASON_TEXT_LOCALES = ["en", "pt-BR"] as const;
export type ReasonTextLocale = (typeof REASON_TEXT_LOCALES)[number];
export type LocalizedText = Readonly<Record<ReasonTextLocale, string>>;

export interface ReasonCatalogEntry {
  readonly category: ReasonCategory;
  readonly severity: "blocking" | "advisory";
  /**
   * i18n message id of the text shown to the player, or `null` when the player is shown nothing
   * (advisory reasons, request-time integration bugs). One id for the Explorer and the docs (D16).
   */
  readonly playerMessageId: string | null;
  /**
   * Canonical player message per locale (`null` exactly when `playerMessageId` is `null`). Neutral
   * wording that never reveals why the player was blocked. The Explorer catalogs
   * (`apps/hub/src/locales`) and the integration docs reason page are derived from it.
   */
  readonly playerMessage: LocalizedText | null;
  /** Recommended action for the operator's team. */
  readonly operatorAction: string;
  /** Request-time errors (e.g. at token issuance) never appear in a verification's reasons. */
  readonly scope: "verification" | "request";
}

/** Single reason catalog (spec risk-verification "Module 1 checks and reason catalog"). */
export const REASON_CATALOG = {
  outside_jurisdiction: {
    category: "location",
    severity: "blocking",
    playerMessageId: "reason.outside_jurisdiction",
    playerMessage: {
      en: "Betting is not available in your location.",
      "pt-BR": "Apostas não estão disponíveis na sua localização.",
    },
    operatorAction: "Player is outside the allowed jurisdictions; do not allow regulated actions.",
    scope: "verification",
  },
  impossible_travel: {
    category: "location",
    severity: "blocking",
    playerMessageId: "reason.impossible_travel",
    playerMessage: {
      en: "We could not confirm your location. Please contact support.",
      "pt-BR": "Não conseguimos confirmar sua localização. Entre em contato com o suporte.",
    },
    operatorAction:
      "The distance from the player's last allowed location cannot be covered in the elapsed time; do not allow regulated actions and review the account for shared or compromised credentials.",
    scope: "verification",
  },
  state_not_determinable: {
    category: "location",
    severity: "blocking",
    playerMessageId: "reason.state_not_determinable",
    playerMessage: {
      en: "Allow location access in your browser and try again.",
      "pt-BR": "Permita o acesso à localização no navegador e tente novamente.",
    },
    operatorAction:
      "The state cannot be determined (IP-only location, or a browser point in no state area) and the environment allows only some states; ask the player to enable browser location and retry.",
    scope: "verification",
  },
  location_check_not_enabled: {
    category: "availability",
    severity: "blocking",
    playerMessageId: "reason.location_check_not_enabled",
    playerMessage: {
      en: "We cannot verify your location right now.",
      "pt-BR": "Não conseguimos verificar sua localização agora.",
    },
    operatorAction:
      "A required check is not enabled for this environment; enable it before go-live.",
    scope: "verification",
  },
  geoip_unavailable: {
    category: "availability",
    severity: "blocking",
    playerMessageId: "reason.geoip_unavailable",
    playerMessage: {
      en: "We could not determine your location. Enable location access and try again.",
      "pt-BR":
        "Não conseguimos determinar sua localização. Permita o acesso à localização e tente novamente.",
    },
    operatorAction:
      "Location could not be resolved; ask the player to retry or enable browser location.",
    scope: "verification",
  },
  player_blocked: {
    category: "blocklist",
    severity: "blocking",
    playerMessageId: "reason.player_blocked",
    playerMessage: {
      en: "This action is not available for your account.",
      "pt-BR": "Esta ação não está disponível para a sua conta.",
    },
    operatorAction: "The player is on the blocklist; review the block in the Explorer.",
    scope: "verification",
  },
  device_blocked: {
    category: "blocklist",
    severity: "blocking",
    playerMessageId: "reason.device_blocked",
    playerMessage: {
      en: "This action is not available on this device.",
      "pt-BR": "Esta ação não está disponível neste dispositivo.",
    },
    operatorAction: "The device is on the blocklist; review the block in the Explorer.",
    scope: "verification",
  },
  integrity_failed: {
    category: "integrity",
    severity: "blocking",
    playerMessageId: "reason.integrity_failed",
    playerMessage: {
      en: "We could not complete the check. Try again.",
      "pt-BR": "Não conseguimos concluir a verificação. Tente novamente.",
    },
    operatorAction:
      "The SDK version is malformed or below the supported minimum; upgrade the Browser Guard SDK.",
    scope: "verification",
  },
  device_ref_missing: {
    category: "integrity",
    severity: "advisory",
    playerMessageId: null,
    playerMessage: null,
    operatorAction:
      "No valid device reference (absent, e.g. storage unavailable, or malformed); the verification still completed.",
    scope: "verification",
  },
  automation_detected: {
    category: "integrity",
    severity: "advisory",
    playerMessageId: null,
    playerMessage: null,
    operatorAction:
      "Automation indicators (e.g. navigator.webdriver) were seen; investigate if repeated.",
    scope: "verification",
  },
  player_ref_looks_like_pii: {
    category: "integrity",
    severity: "blocking",
    playerMessageId: null,
    playerMessage: null,
    operatorAction: "Send a pseudonymous player_ref, never CPF, e-mail or phone.",
    scope: "request",
  },
} as const satisfies Record<string, ReasonCatalogEntry>;

export type ReasonCode = keyof typeof REASON_CATALOG;
export const REASON_CODES = Object.keys(REASON_CATALOG) as ReasonCode[];
export const ReasonCodeSchema = z
  .enum(REASON_CODES as [ReasonCode, ...ReasonCode[]])
  .meta({ id: "ReasonCode" });

/** Throws when a code is used without a catalog entry (guards codes coming from untyped sources). */
export function assertReasonInCatalog(code: string): asserts code is ReasonCode {
  if (!Object.hasOwn(REASON_CATALOG, code)) {
    throw new Error(`Reason "${code}" has no entry in the reason catalog (packages/contracts)`);
  }
}

export const Reason = z
  .object({
    code: ReasonCodeSchema,
    category: ReasonCategory,
    severity: z.enum(["blocking", "advisory"]),
  })
  .meta({ id: "Reason" });
export type Reason = z.infer<typeof Reason>;

export function reason(code: ReasonCode): Reason {
  const entry = REASON_CATALOG[code];
  return { code, category: entry.category, severity: entry.severity };
}
